<script setup lang="ts">
const { getMerchantAttributes } = useLuxuryDesign();
const itemDesign = computed(() => getMerchantAttributes(props.item.merchantId));

const props = defineProps({
  item: {
    type: Object as PropType<Item>,
    required: true,
  },
});

const appStateStore = useAppStateStore();
const timeRemaining = ref<number>(0);
const isTimeRemaining = computed(() => timeRemaining.value > 0);
let countdownInterval: ReturnType<typeof setInterval>;

const checkNextTime = () => {
  if (props.item.nextTime) {
    const now = new Date().getTime();
    const next = new Date(props.item.nextTime).getTime();
    timeRemaining.value = next - now;

    countdownInterval = setInterval(() => {
      timeRemaining.value -= 1000;

      if (timeRemaining.value <= 0) {
        clearInterval(countdownInterval);
        timeRemaining.value = 0;
      }
    }, 1000);
  }
};

const showStats = computed(() => {
  return Object.keys(props.item.properties || {}).length > 0;
});

const isItemComingSoon = computed(() => {
  if (props.item.start) {
    const startDate = new Date(props.item.start);
    const now = new Date();
    return startDate > now;
  }
  return false;
});

const creationNumber = computed(() =>
  isItemComingSoon.value ? "?" : props.item.number ?? undefined
);

const getPriceText = computed(() => {
  if (isItemComingSoon.value) {
    return "main.cardCollectible.startingAt";
  }

  if (timeRemaining.value > 0) {
    return "main.cardCollectible.buySoon";
  }

  if (props.item.number === 0) {
    return "main.cardCollectible.lastSold";
  }

  if (props.item.nextPrice) {
    return "main.cardCollectible.buyNow";
  }

  return "main.cardConsumable.buyFor";
});

const isEliteItem = computed(() => {
  const materials = props.item?.properties?.materials?.value;
  if (Array.isArray(materials)) {
    return materials.length > 0;
  }
  return Boolean(materials);
});

onMounted(() => {
  checkNextTime();
});

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  appStateStore.currentMerchant = props.item.merchantId as MerchantId;
});
</script>

<template>
  <div
    v-bind="itemDesign"
    class="border-2 border-primary-900 rounded-lg merchant-card">
    <div class="xl:grid xl:grid-cols-2">
      <CardDetailImage
        :merchantId="item.merchantId"
        :rarity="item.rarity ? item.rarity : ''"
        :image-id="item.imageId"
        :is-time-remaining="isTimeRemaining"
        :number="
          item.number !== undefined && item.number !== null
            ? item.number
            : undefined
        "
        :copies="item.totalCopies ? item.totalCopies : undefined"
        :level="item.level ? item.level : undefined"
        :low-supply-threshold="item.lowSupplyThreshold"
        :properties="item.properties"
        :start-date="item.start" />
      <div
        class="relative lg:col-start-2 lg:row-span-3 flex flex-col border-t border-l-0 xl:border-l xl:border-t-0 border-primary-900">
        <CardDetailShare />
        <CardDetailTitle
          :id="item.id"
          :translation-id="item.translationId"
          :merchantId="item.merchantId"
          :categoryId="item.categoryId"
          :one-time="item.oneTime"
          :offer-text="
            item.oneTime && $te(`${item.translationId}.offer`)
              ? $t(`${item.translationId}.offer`)
              : ''
          "
          :number="item.number ?? 0"
          :total="item.totalCopies ?? 0" />
        <CardDetailSupply
          v-if="item.totalCopies && !isItemComingSoon"
          :supply="item.totalCopies"
          :number="item.number ?? 0" />
        <div
          v-if="
            item.level !== undefined ||
            (item.number !== undefined && !isEliteItem)
          "
          class="px-7 py-4 border-b-2 border-primary-900">
          <CardDetailMeta
            :number="!isEliteItem ? item.number ?? undefined : undefined"
            :display-number="!isEliteItem ? creationNumber : undefined"
            :level="item.level ?? undefined" />
        </div>
        <CardDetailDescription
          :translationId="item.translationId"
          :merchantId="item.merchantId" />
        <CardDetailProperties
          v-if="
            item.type == 'COLLECTIBLE' && item.merchantId !== 'proof-of-luxe'
          "
          :rarity="item.rarity ? item.rarity : ''"
          :image-id="item.imageId"
          :properties="item.properties"
          :merchant-id="item.merchantId" />
        <div>
          <CardDetailPrice
            :price="item.price"
            :original-price="item.originalPrice"
            :text="$t(getPriceText)"
            :next-price="
              item.number === 1 || item.number === 0
                ? undefined
                : item.nextPrice
            "
            :start-date="item.start" />
          <div class="pl-7 item-detail__actions">
            <div
              v-if="item.start && isItemComingSoon"
              class="py-5 sm:pr-0 border-primary-900 flex flex-col sm:flex-row gap-1 sm:flex-wrap">
              <NuxtIcon
                name="mdi:clock-outline"
                class="text-neutral-200 w-6 h-6" />
              <p class="text-secondary-200">{{ $t(`main.availableOn`) }}</p>
              <p class="text-neutral-200 font-semibold">
                {{
                  new Date(item.start).toLocaleDateString($i18n.locale, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }}
              </p>
            </div>
            <CardDetailBuyButton v-else :item="item" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <CardDetailStats
    v-if="showStats"
    class="card-detail-stats--store"
    :stats="item.properties"
    :merchant-id="item.merchantId" />
  <CardDetailLegacy
    v-if="showStats"
    :stats="item.properties"
    :merchant-id="item.merchantId" />
</template>
